import {
  captureCopilotUiOpen,
  type CopilotUiReadinessView,
  isUiReadinessEnabled,
} from '@github-ui/copilot-browser-latency/copilot-ui-readiness'
import {emitOpenCopilotChatV2} from '@github-ui/copilot-chat-app-embedded/emit-open-chat-event'
import {
  logCopilotChatRouting,
  OpenCopilotChatEvent,
  publishOpenCopilotChat,
  subscribeOpenCopilotChat,
  subscribePreloadCopilotChat,
} from '@github-ui/copilot-chat-events'
import {
  claimCopilotChatAppAvailability,
  copilotChatHeaderButtonID,
  copilotDockedPanelClosedEvent,
  getCopilotChatPolicyVersion,
  getDockedPanelOwner,
  getOpenCopilotChatVersion,
  requestDockedPanelHandoff,
} from '@github-ui/copilot-chat-helpers/constants'
import {isCopilotPath} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {
  isNoticeDismissedForPage,
  ONE_COPILOT_RELAUNCH_ANNOUNCEMENT_NOTICE,
  registerPendingOnboardingAnnouncement,
  useActiveOnboardingSurface,
} from '@github-ui/copilot-chat-helpers/copilot-onboarding'
import {getDockedPageReady, subscribeDockedPageReady} from '@github-ui/copilot-chat-helpers/docked-page-store'
import {
  readPersistedChatMinimizedState,
  usePersistedChatOpenState,
} from '@github-ui/copilot-chat-helpers/embedded-chat-open-state'
import {CopilotChatIntents} from '@github-ui/copilot-chat-types'
import {isFeatureEnabled, isFeatureEnabledWithOptOut} from '@github-ui/feature-flags'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {GlobalCopilotMenu} from '@github-ui/global-copilot-menu/GlobalCopilotMenu'
import {getQueryClient} from '@github-ui/query-client'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useLocation} from '@github-ui/react-router'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useMediaQuery} from '@github-ui/use-media-query'
import {CopilotIcon} from '@primer/octicons-react'
import {ButtonGroup} from '@primer/react'
import {clsx} from 'clsx'
import {lazy, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'

import {NavLinkItem} from '../../primitives/NavItemLink'
import baseStyles from '../../primitives/styles.module.css'
import {useTopNavConfig} from '../../primitives/TopNav'
import {useGlobalNavAnalytics} from '../../utils/Analytics'
import {useDeferredPayloadSlice} from '../../utils/DeferredNavPayloadContext'
import {LazyGlobalNavAgentsMenu} from '../LazyGlobalNavAgentsMenu'
import {CopilotChatLoader} from './CopilotChatLoader'
import styles from './CopilotItems.module.css'

export type CopilotItemsProps = {
  show?: boolean
  showAgentsButton?: boolean
  copilotApiUrl?: string | undefined
  copilotChatUrl?: string
  showRelaunchAnnouncement?: boolean
}

const LazyCopilotRelaunchAnnouncement = lazy(() => import('./CopilotRelaunchAnnouncement'))

function AnnouncementLoadFailure({onAvailabilityResolved}: {onAvailabilityResolved: () => void}) {
  useEffect(() => onAvailabilityResolved(), [onAvailabilityResolved])
  return null
}

function DeferredRelaunchAnnouncement({
  anchor,
  portalSlot,
  hasAgentsMenu,
  freePopoverSettled,
  handoffPending,
  onDismiss,
  onHandoffEnd,
  registerDismiss,
  onLearnMore,
  onAvailabilityResolved,
  onVisibilityChange,
  offerTour,
}: {
  anchor: HTMLAnchorElement
  portalSlot: HTMLElement
  hasAgentsMenu: boolean
  freePopoverSettled: boolean
  handoffPending: boolean
  onDismiss: () => void
  onHandoffEnd: () => void
  registerDismiss: (dismiss: (() => void) | null) => void
  onLearnMore: () => void
  onAvailabilityResolved: () => void
  onVisibilityChange: (visible: boolean) => void
  offerTour: boolean
}) {
  const {data: agentsMenu, isPending} = useDeferredPayloadSlice('agents_menu')
  const activeSurface = useActiveOnboardingSurface()
  const [freePopoverShown, setFreePopoverShown] = useState(false)
  const [freePopoverTimedOut, setFreePopoverTimedOut] = useState(false)
  if (activeSurface === 'free-user-popover' && !freePopoverShown) setFreePopoverShown(true)
  const waitingForFreePopover =
    hasAgentsMenu && (isPending || (agentsMenu?.freeUserPopoverAvailable && !freePopoverShown && !freePopoverSettled))
  useEffect(() => {
    if (!hasAgentsMenu || isPending || !agentsMenu?.freeUserPopoverAvailable || freePopoverShown || freePopoverSettled)
      return
    const timeout = setTimeout(() => setFreePopoverTimedOut(true), 5000)
    return () => clearTimeout(timeout)
  }, [agentsMenu?.freeUserPopoverAvailable, freePopoverSettled, freePopoverShown, hasAgentsMenu, isPending])
  useEffect(() => {
    if (activeSurface !== null) onAvailabilityResolved()
  }, [activeSurface, onAvailabilityResolved])

  if (
    (waitingForFreePopover && (isPending || !freePopoverTimedOut)) ||
    (activeSurface === 'free-user-popover' && !handoffPending)
  )
    return null

  return (
    <ErrorBoundary
      boundaryName="copilot-relaunch-announcement"
      fallback={<AnnouncementLoadFailure onAvailabilityResolved={onAvailabilityResolved} />}
    >
      <Suspense fallback={null}>
        <LazyCopilotRelaunchAnnouncement
          anchor={anchor}
          portalSlot={portalSlot}
          onDismiss={onDismiss}
          onHandoffEnd={onHandoffEnd}
          registerDismiss={registerDismiss}
          onLearnMore={onLearnMore}
          onAvailabilityResolved={onAvailabilityResolved}
          onVisibilityChange={onVisibilityChange}
          offerTour={offerTour}
        />
      </Suspense>
    </ErrorBoundary>
  )
}

// Matches the modifier rules in packages/react-router/tanstack-router/use-link-click-handler.ts
// so the button only intercepts the clicks the router would have navigated itself.
function isPlainLeftClick(event: React.MouseEvent): boolean {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  return event.button === 0 && !(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)
}

export function CopilotItems({
  show,
  showAgentsButton,
  copilotApiUrl,
  copilotChatUrl,
  showRelaunchAnnouncement,
}: CopilotItemsProps) {
  const {pathname} = useLocation()
  const isCopilotPage = isCopilotPath(pathname)
  const {loadCopilotChat} = useTopNavConfig()
  const {sendItemClick} = useGlobalNavAnalytics('COPILOT')
  const isChatAppEmbedded = isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out')
  const isChatAppAvailable = Boolean(show) && isChatAppEmbedded && Boolean(copilotChatUrl) && !isCopilotPage
  const showOneCopilotNav = isChatAppEmbedded && showAgentsButton
  const shouldShowUnifiedChatButton = isChatAppEmbedded && Boolean(copilotChatUrl)
  // Example: a user minimizes Copilot on an issue, then follows a full-page link
  // to a PR or file. The new page forgets that the chat code was already loaded,
  // but this browser tab still remembers that Copilot was minimized. Use that
  // saved state to recreate the minimized bar without another click.
  const shouldRestoreMinimizedChat = copilotLocalStorage.getMinimizedState()

  // Defer the Copilot chat loader to the client. `useRuntimeEnvironment` returns the server
  // snapshot during SSR and the first hydration render, then re-renders as `isBrowser` on the
  // client — so the loader (and its data fetch) never runs on the server or causes a hydration
  // mismatch.
  const {isBrowser} = useRuntimeEnvironment()
  const isWideViewport = useMediaQuery('(min-width: 768px)')
  // Store pending open event to re-dispatch after embedded component loads
  const [pendingOpenEvent, setPendingOpenEvent] = useState<OpenCopilotChatEvent | null>(null)
  const [uiReadiness, setUiReadiness] = useState<CopilotUiReadinessView>()
  const chatAppOpenedPathRef = useRef<string | null>(null)
  // Controls the ChatAppEmbedded panel independently from the CopilotImmersiveEmbedded panel
  const [persistedChatAppOpen, setChatAppOpen] = usePersistedChatOpenState()
  const chatAppOpen = isChatAppEmbedded && persistedChatAppOpen
  const [hasUserOpenedChatApp, setHasUserOpenedChatApp] = useState(false)
  const isRestoredChatAppOpen = chatAppOpen && !hasUserOpenedChatApp
  const embeddedChatTriggerRef = useRef<HTMLAnchorElement | HTMLButtonElement | null>(null)
  const embeddedChatLauncherRef = useRef<HTMLDivElement>(null)
  const [announcementAnchor, setAnnouncementAnchor] = useState<HTMLAnchorElement | null>(null)
  const [announcementSlot, setAnnouncementSlot] = useState<HTMLDivElement | null>(null)
  const [announcementVisible, setAnnouncementVisible] = useState(false)
  const [announcementDismissed, setAnnouncementDismissed] = useState(false)
  const [announcementHandoffPending, setAnnouncementHandoffPending] = useState(false)
  const [announcementPending, setAnnouncementPending] = useState(true)
  const [freePopoverSettled, setFreePopoverSettled] = useState(false)
  const dismissAnnouncementRef = useRef<(() => void) | null>(null)
  const dismissAnnouncement = useCallback(() => {
    setAnnouncementHandoffPending(false)
    setAnnouncementDismissed(true)
  }, [])
  const endAnnouncementHandoff = useCallback(() => setAnnouncementHandoffPending(false), [])
  const resolveAnnouncement = useCallback(() => setAnnouncementPending(false), [])
  const resolveFreePopover = useCallback(() => setFreePopoverSettled(true), [])
  const registerAnnouncementDismiss = useCallback((dismiss: (() => void) | null) => {
    dismissAnnouncementRef.current = dismiss
  }, [])
  const announcementEligible =
    isBrowser &&
    isWideViewport &&
    showRelaunchAnnouncement === true &&
    (announcementHandoffPending ||
      (!announcementDismissed && !isNoticeDismissedForPage(ONE_COPILOT_RELAUNCH_ANNOUNCEMENT_NOTICE))) &&
    !isCopilotPage &&
    shouldShowUnifiedChatButton

  useLayoutEffect(() => {
    if (announcementEligible && announcementPending) return registerPendingOnboardingAnnouncement()
  }, [announcementEligible, announcementPending])

  // Full-page Copilot owns chat here. Do not replay an unfinished embedded open
  // or autofocus the restored panel when navigating back to another page.
  if (isCopilotPage) {
    if (pendingOpenEvent) setPendingOpenEvent(null)
    if (hasUserOpenedChatApp) setHasUserOpenedChatApp(false)
  }

  useEffect(() => {
    if (!isChatAppAvailable) return
    return claimCopilotChatAppAvailability()
  }, [isChatAppAvailable])

  // Subscribe to open-copilot-chat events to trigger loading.
  useEffect(() => {
    if (!show || isCopilotPage) return
    return subscribeOpenCopilotChat(e => {
      const openChatVersion = getCopilotChatPolicyVersion({
        chatAppEmbeddedEnabled: isChatAppAvailable,
        source: e.payload.source,
      })
      if (
        isUiReadinessEnabled() &&
        isChatAppAvailable &&
        openChatVersion === 'v2' &&
        (!chatAppOpen || (e.payload && 'newThread' in e.payload && e.payload.newThread === true))
      ) {
        const view = captureCopilotUiOpen(e, {
          surface: 'embedded_chat',
          entryKind: 'embedded',
          repositoryScope: 'unknown',
        })
        if (view) {
          uiReadiness?.dispose('replaced')
          const queryClient = getQueryClient()
          const cached = queryClient.getQueryData<{deferredPayloadUrl?: string}>(['copilot-chat', copilotChatUrl])
          if (
            cached &&
            (!cached.deferredPayloadUrl ||
              queryClient.getQueryData(['copilot-deferred-payload', cached.deferredPayloadUrl]))
          ) {
            view.configurationAvailable(view.startedAt, true)
          }
          setUiReadiness(view)
        }
      }
      loadCopilotChat()
      const dockedPanelOwner = getDockedPanelOwner()
      logCopilotChatRouting('open event received', {
        id: e.payload.id,
        intent: e.payload.intent,
        source: e.payload.source,
        newThread: 'newThread' in e.payload ? e.payload.newThread : undefined,
        referenceCount: e.payload.references?.length ?? 0,
        chatAppEmbeddedEnabled: isChatAppEmbedded,
        copilotChatUrlPresent: Boolean(copilotChatUrl),
        selectedVersion: openChatVersion,
        dockedPanelOwner,
        dockedPageReady: getDockedPageReady(),
        currentPath: window.location.pathname,
      })
      // When the chat-app-embedded flag is on, page-specific buttons (PR header,
      // explain file, etc.) should open the ChatApp UI.
      if (openChatVersion === 'v2') {
        logCopilotChatRouting('routing event to Chat v2', {
          id: e.payload.id,
          dockedPanelOwner,
        })
        chatAppOpenedPathRef.current = window.location.pathname
        if (copilotChatUrl && getDockedPageReady()) requestDockedPanelHandoff('chatapp')
        // v2 (ChatAppEmbedded) will handle this open. Emit `copilot.open_copilot_chat`
        // here so the shared event stream (v1 emits from
        // `CopilotChatManager.handleOpenPanelEvent`) covers v2 opens too.
        if (e.payload.source !== 'header') {
          emitOpenCopilotChatV2(e)
        }
        setChatAppOpen(true)
        setPendingOpenEvent(e)
      } else {
        logCopilotChatRouting('routing event to immersive chat', {
          id: e.payload.id,
          dockedPanelOwner,
          willCloseChatApp: dockedPanelOwner !== 'chatapp',
        })
        // Only clear `chatAppOpen` when chatapp hasn't already claimed the
        // retained docked-panel slot. Once chatapp owns the claim the
        // immersive UI won't take it over (see `getDockedPanelOwner()`
        // gate in `DockedCopilotChatPortal`), so hiding chatapp here would
        // leave a header-button reopen with an empty `#copilot-chat-docked-content`
        // element — no surface portals into it.
        if (getDockedPanelOwner() !== 'chatapp') {
          chatAppOpenedPathRef.current = null
          setChatAppOpen(false)
        }
        setPendingOpenEvent(e)
      }
    })
  }, [
    copilotChatUrl,
    loadCopilotChat,
    isChatAppAvailable,
    isChatAppEmbedded,
    isCopilotPage,
    setChatAppOpen,
    show,
    chatAppOpen,
    uiReadiness,
  ])

  useEffect(() => {
    if (chatAppOpen) return
    uiReadiness?.dispose()
  }, [chatAppOpen, uiReadiness])

  useEffect(() => {
    if (!isChatAppEmbedded || isCopilotPage) return

    const clearExpandedChatOnDockedPage = () => {
      const dockedPageReady = getDockedPageReady()
      const minimized = readPersistedChatMinimizedState()
      const dockedPanelOwner = getDockedPanelOwner()
      const openedPath = chatAppOpenedPathRef.current
      const shouldClear =
        dockedPageReady && !minimized && openedPath !== window.location.pathname && dockedPanelOwner !== 'chatapp'

      logCopilotChatRouting('evaluating docked-page state', {
        dockedPageReady,
        minimized,
        dockedPanelOwner,
        openedPath,
        currentPath: window.location.pathname,
        willCloseChatApp: shouldClear,
      })

      if (shouldClear) {
        chatAppOpenedPathRef.current = null
        setChatAppOpen(false)
      }
    }
    const clearChatOnDockClose = () => {
      // Panel-closed events are global, so only clear ChatApp's persisted open
      // state when ChatApp actually owned the dock — otherwise this would also
      // dismiss a minimized ChatApp when an unrelated immersive-owned panel closes.
      const dockedPanelOwner = getDockedPanelOwner()
      logCopilotChatRouting('docked panel closed', {
        dockedPanelOwner,
        willCloseChatApp: dockedPanelOwner === 'chatapp',
      })
      if (dockedPanelOwner !== 'chatapp') return
      chatAppOpenedPathRef.current = null
      setChatAppOpen(false)
    }

    const unsubscribeDockedPageReady = subscribeDockedPageReady(clearExpandedChatOnDockedPage)
    document.addEventListener(copilotDockedPanelClosedEvent, clearChatOnDockClose)
    clearExpandedChatOnDockedPage()

    return () => {
      unsubscribeDockedPageReady()
      document.removeEventListener(copilotDockedPanelClosedEvent, clearChatOnDockClose)
    }
  }, [isChatAppEmbedded, isCopilotPage, setChatAppOpen])

  // Subscribe to preload-copilot-chat events to warm up the chat component without opening it.
  useEffect(() => {
    if (isCopilotPage) return
    return subscribePreloadCopilotChat(() => {
      loadCopilotChat()
    })
  }, [isCopilotPage, loadCopilotChat])

  useEffect(() => {
    if (shouldRestoreMinimizedChat && !isCopilotPage) {
      // Keep the chat code loaded for the rest of this page visit. Clicking
      // Restore removes the saved minimized state, but the chat code must remain
      // loaded while either the floating dialog or the right-side panel opens.
      loadCopilotChat()
    }
  }, [isCopilotPage, loadCopilotChat, shouldRestoreMinimizedChat])

  if (!show) {
    return null
  }

  const handleCopilotIconClick = (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    entryPointId: string = 'copilot-assistive',
  ) => {
    e.preventDefault()
    loadCopilotChat()
    publishOpenCopilotChat({
      id: entryPointId,
      intent: CopilotChatIntents.conversation,
      newThread: true,
      source: 'header',
    })
  }

  const openEmbeddedChat = (trigger: HTMLAnchorElement | HTMLButtonElement, newThread = false) => {
    embeddedChatTriggerRef.current = trigger
    setHasUserOpenedChatApp(true)
    loadCopilotChat()
    // Publish an event so the side panel integration (on docked pages) can
    // intercept it and open the docked panel. The subscribeOpenCopilotChat
    // handler routes non-header sources to setChatAppOpen.
    publishOpenCopilotChat({
      id: 'copilot-chat-app',
      intent: CopilotChatIntents.conversation,
      ...(newThread ? {newThread: true} : {}),
    })
  }

  const handleOpenEmbeddedChat = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    e.preventDefault()
    openEmbeddedChat(e.currentTarget)
  }

  const preloadUnifiedChat = () => {
    // The v1 header link has never preloaded, so only warm the chat bundle when
    // this control would open v2.
    if (!isCopilotPage && getOpenCopilotChatVersion({chatAppEmbeddedEnabled: isChatAppEmbedded}) === 'v2') {
      loadCopilotChat()
    }
  }

  const copilotItemProps = {
    href: '/copilot',
    icon: CopilotIcon,
    'aria-label': 'Chat with Copilot',
    analyticsKey: 'COPILOT_CHAT',
    analyticsSubCategory: 'COPILOT',
    copilotApiUrl,
    onClick: isCopilotPage ? undefined : handleCopilotIconClick,
  }

  return (
    <>
      <div className={shouldShowUnifiedChatButton ? styles.NarrowLeadingSpace : 'hide-sm hide-md'}>
        <div className={styles.Wrapper}>
          <ButtonGroup
            className={announcementEligible ? styles.AnnouncementGroup : undefined}
            data-announcement-visible={announcementEligible && announcementVisible ? '' : undefined}
          >
            {shouldShowUnifiedChatButton ? (
              <div ref={embeddedChatLauncherRef} style={{display: 'contents'}}>
                <NavLinkItem
                  id={copilotChatHeaderButtonID}
                  ref={announcementEligible ? setAnnouncementAnchor : undefined}
                  href="/copilot"
                  icon={CopilotIcon}
                  aria-label="Chat with Copilot"
                  analyticsKey={e =>
                    !isCopilotPage &&
                    isPlainLeftClick(e) &&
                    getOpenCopilotChatVersion({chatAppEmbeddedEnabled: isChatAppEmbedded}) === 'v2'
                      ? 'COPILOT_EMBEDDED_CHAT'
                      : 'COPILOT_CHAT'
                  }
                  analyticsSubCategory="COPILOT"
                  onClick={e => {
                    if (isCopilotPage) return
                    dismissAnnouncementRef.current?.()
                    if (!isPlainLeftClick(e)) return
                    // Resolve again because the panel owner can change before a click.
                    const isV2 = getOpenCopilotChatVersion({chatAppEmbeddedEnabled: isChatAppEmbedded}) === 'v2'
                    if (isV2) {
                      handleOpenEmbeddedChat(e)
                    } else {
                      handleCopilotIconClick(e, copilotChatHeaderButtonID)
                    }
                  }}
                  onMouseEnter={preloadUnifiedChat}
                  onFocus={preloadUnifiedChat}
                />
                {announcementEligible && (
                  <div ref={setAnnouncementSlot} className={styles.AnnouncementSlot}>
                    <span aria-hidden="true" />
                  </div>
                )}
              </div>
            ) : (
              <NavLinkItem {...copilotItemProps} />
            )}
            {showOneCopilotNav ? (
              <LazyGlobalNavAgentsMenu
                copilotApiUrl={copilotApiUrl}
                showRelaunchAnnouncement={showRelaunchAnnouncement}
                onFreePopoverSettled={announcementEligible ? resolveFreePopover : undefined}
                isOneCopilotNav
                anchorProps={{
                  variant: 'invisible',
                  className: clsx(
                    baseStyles.appHeaderButton,
                    styles.OneCopilotMenu,
                    shouldShowUnifiedChatButton && 'hide-sm hide-md',
                  ),
                  onClick: () => {
                    sendItemClick('AGENTS_MENU')
                  },
                }}
              />
            ) : (
              <GlobalCopilotMenu
                anchorProps={{
                  variant: 'invisible',
                  className: clsx(
                    baseStyles.appHeaderButton,
                    styles.CopilotMenu,
                    shouldShowUnifiedChatButton && 'hide-sm hide-md',
                  ),
                  onMouseEnter: loadCopilotChat,
                  onFocus: loadCopilotChat,
                  onClick: () => {
                    loadCopilotChat()
                    sendItemClick('COPILOT_MENU')
                  },
                }}
              />
            )}
          </ButtonGroup>
          {!showOneCopilotNav && showAgentsButton && (
            <LazyGlobalNavAgentsMenu
              copilotApiUrl={copilotApiUrl}
              showRelaunchAnnouncement={showRelaunchAnnouncement}
              onFreePopoverSettled={announcementEligible ? resolveFreePopover : undefined}
              anchorProps={{
                variant: 'invisible',
                className: clsx(baseStyles.appHeaderButton, styles.AgentsMenu, styles.AgentsMenuStandalone),
                onClick: () => {
                  sendItemClick('AGENTS_MENU')
                },
              }}
            />
          )}
          {announcementEligible && announcementAnchor && announcementSlot && (
            <DeferredRelaunchAnnouncement
              anchor={announcementAnchor}
              portalSlot={announcementSlot}
              hasAgentsMenu={Boolean(showAgentsButton)}
              freePopoverSettled={freePopoverSettled}
              handoffPending={announcementHandoffPending}
              onDismiss={dismissAnnouncement}
              onHandoffEnd={endAnnouncementHandoff}
              registerDismiss={registerAnnouncementDismiss}
              onLearnMore={() => {
                setAnnouncementHandoffPending(true)
                openEmbeddedChat(announcementAnchor, true)
              }}
              onAvailabilityResolved={resolveAnnouncement}
              onVisibilityChange={setAnnouncementVisible}
              offerTour={
                getOpenCopilotChatVersion({chatAppEmbeddedEnabled: isChatAppEmbedded}) === 'v2' &&
                isFeatureEnabled('one_copilot_environment_strip_tour')
              }
            />
          )}
        </div>
      </div>
      {/*
        The chat loader is gated on `isBrowser` so it (and its data fetch) never runs on the server
        or during the first hydration render, keeping this subtree hydration-safe. It then mounts
        eagerly on non-Copilot pages whenever a chat payload URL is available.
        The loader uses saved minimized state to recreate the minimized Copilot bar after navigating
        from an issue to a PR with a full page load.
      */}
      {isBrowser && !isCopilotPage && copilotChatUrl ? (
        <CopilotChatLoader
          uiReadiness={uiReadiness}
          copilotChatUrl={copilotChatUrl}
          pendingOpenEvent={pendingOpenEvent}
          onPendingEventHandled={() => setPendingOpenEvent(null)}
          chatAppOpen={chatAppOpen}
          onChatAppOpen={() => setChatAppOpen(true)}
          onChatAppClose={() => {
            chatAppOpenedPathRef.current = null
            setChatAppOpen(false)
            const fallbackFocusTarget = embeddedChatLauncherRef.current?.querySelector<
              HTMLAnchorElement | HTMLButtonElement
            >('a, button')
            ;(embeddedChatTriggerRef.current ?? fallbackFocusTarget)?.focus()
          }}
          disableAutoFocus={isRestoredChatAppOpen}
        />
      ) : null}
    </>
  )
}
